import { useEffect, useState } from 'react';

import { resolveRootFiSdkLoaderScriptUrl } from '../helpers/RootfiLinkConstants';
import type {
  RootfiLinkCallbackData,
  RootfiLinkOptions,
} from '../types/rootfiLink';

// shared callback for each hook instance
let _onSuccessCallback: ((data: RootfiLinkCallbackData) => void) | undefined;
let _onExitCallback: (() => void) | undefined;

export type {
  RootfiEnvironment,
  RootfiLinkCallbackData,
  RootfiLinkOptions,
} from '../types/rootfiLink';

interface RootfiWindowSdk {
  initialize: (config: Record<string, unknown>) => void;
  openLink: (inviteLinkId?: string) => void;
  closeLink: () => void;
}

function getRootfiWindowSdk(): RootfiWindowSdk | undefined {
  return (window as unknown as { RootfiLink?: RootfiWindowSdk })?.RootfiLink;
}

export function useRootfiLink(options: RootfiLinkOptions) {
  const [isReady, setIsReady] = useState(false);

  const openLink = (inviteLinkId?: string) => {
    _onSuccessCallback = options.onSuccess;
    _onExitCallback = options.onExit;
    const sdk = getRootfiWindowSdk();
    if (sdk) sdk.openLink(inviteLinkId);
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src = resolveRootFiSdkLoaderScriptUrl(
      options.environment ?? 'global'
    );

    script.onload = () => {
      const sdk = getRootfiWindowSdk();
      const { showConsentScreen, environment } = options;
      sdk?.initialize({
        environment,
        showConsentScreen,
        onExit: () => _onExitCallback?.(),
        onSuccess: (callbackData: { data: RootfiLinkCallbackData }) =>
          _onSuccessCallback?.(callbackData.data),
        onReady: () => {
          setIsReady(true);
        },
      });
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
      setIsReady(false);
    };
    // Load the SDK once per environment; invite-link tokens are passed per open via
    // `openLink(inviteLinkId)` so creating a link in a modal never remounts the script.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable env key only
  }, [options.environment]);

  return {
    openLink,
    closeLink: () => getRootfiWindowSdk()?.closeLink(),
    isReady,
  };
}
